import type {
  AuthResponse,
  LogoutResponse,
  PlayerResponse,
  PlayerStatsResponse,
  FriendStatsResponse,
  PaymentStatusResponse,
  PaymentSubmitResponse,
  PaymentMethod,
  FriendInfo,
  BattleTimerResponse,
  BattlePhase,
} from '@vikings/types';
import { BATTLE_CYCLE } from '@vikings/game-data';
import { getInitData } from '../telegram/init';

//import.meta.env.DEV ? 'https://worker.vikings-quest.online' : 'https://worker.vikings-quest.online';
const API_BASE = 'https://worker.vikings-quest.online';

// ── Local dev mock state ──
const DEV_FRIENDS: FriendInfo[] = [
  { name: 'Ivan', characterSeed: '67890', phone: '+380507654321', code: 'BUF001', buffIndex: 1 },
  { name: 'Bjorn', characterSeed: '11223', phone: '+380509998877', code: 'BUF002', buffIndex: 2 },
  { name: 'Astrid', characterSeed: '44556', phone: '+380503332211', code: 'BUF003', buffIndex: 3 },
];

const DEV_FRIEND_STATS: Record<
  string,
  { first: number; final: number; tax: number; buffIndex: 1 | 2 | 3 }
> = {
  Ivan: { first: 50, final: 80, tax: 20, buffIndex: 1 },
  Bjorn: { first: 30, final: 60, tax: 15, buffIndex: 2 },
  Astrid: { first: 70, final: 110, tax: 40, buffIndex: 3 },
};

let devPaymentStatus: PaymentStatusResponse = {
  required: true,
  amount: 100,
  currency: 'coins',
  status: 'pending',
  coins_sac: 100,
  cabbage_sac: 50,
  star_sac: 25,
  tokens_sac: 10,
};

function isDevMock(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return import.meta.env.DEV && (params.has('mock') || params.has('dev'));
}

/**
 * Make an authenticated API request.
 * Attaches Telegram initData to every request.
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const initData = getInitData();

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': initData,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(
      (error as { error?: string }).error ?? `HTTP ${response.status}`,
      response.status,
    );
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

// ═══════════════════════════════════════════════
// API methods
// ═══════════════════════════════════════════════

export async function authenticate(code?: string): Promise<AuthResponse> {
  const VALHALLA_WARNING = 'тільки обрані потраплять у валгалу, але ти не один із них';

  if (isDevMock()) {
    if (!code) {
      return {
        success: false,
        player: null,
        error: VALHALLA_WARNING,
      };
    }
    const clean = code.trim().toUpperCase();
    if (clean === 'VIK001' || clean === 'VIKINGS' || clean === '12345') {
      return {
        success: true,
        player: {
          name: 'Ragnar',
          phone: '+380501234567',
          code: 'VIK001',
          role: 'player',
          character: { seed: '12345' },
          friend: DEV_FRIENDS[0]!,
          friends: DEV_FRIENDS,
        },
      };
    }
    return {
      success: false,
      player: null,
      error: VALHALLA_WARNING,
    };
  }
  const initData = getInitData();
  if (!initData && !code) {
    return {
      success: false,
      player: null,
      error: VALHALLA_WARNING,
    };
  }
  return apiRequest<AuthResponse>('/api/auth', {
    method: 'POST',
    body: code ? JSON.stringify({ code }) : undefined,
  });
}

export async function logout(): Promise<LogoutResponse> {
  if (isDevMock()) {
    return { success: true };
  }
  return apiRequest<LogoutResponse>('/api/auth/logout', {
    method: 'POST',
  });
}

export async function getPlayer(): Promise<PlayerResponse> {
  if (isDevMock()) {
    return {
      name: 'Ragnar',
      phone: '+380501234567',
      code: 'VIK001',
      role: 'player',
      character: { seed: '12345' },
      friend: DEV_FRIENDS[0]!,
      friends: DEV_FRIENDS,
    };
  }
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return apiRequest<PlayerResponse>(`/api/player${search}`);
}

export async function getPlayerStats(): Promise<PlayerStatsResponse> {
  if (isDevMock()) {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return {
      month,
      coins_first_player: 150,
      coins_final_player: 200,
      coins_tax_player: 450,
      coins_first: 150,
      coins_final: 200,
      coins_tax: 450,
      coinsSpent: 450,
    };
  }
  let query = '';
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) query = `?code=${encodeURIComponent(code)}`;
  }
  return apiRequest<PlayerStatsResponse>(`/api/player/stats${query}`);
}

export async function getFriendStats(
  friendName?: string,
  buffIndex?: number,
  friendCode?: string,
): Promise<FriendStatsResponse> {
  if (isDevMock()) {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const name = friendName ?? 'Ivan';
    const mock = DEV_FRIEND_STATS[name] ?? {
      first: 50,
      final: 80,
      tax: 20,
      buffIndex: (buffIndex ?? 1) as 1 | 2 | 3,
    };
    const bIdx = (buffIndex ?? mock.buffIndex) as 1 | 2 | 3;
    const res: FriendStatsResponse = {
      name,
      month,
      buffIndex: bIdx,
      coins_first_buff: mock.first,
      coins_final_buff: mock.final,
      coins_tax_buff: mock.tax,
      coinsSpent: mock.tax,
    };
    if (bIdx === 1) {
      res.coins_first_buff1 = mock.first;
      res.coins_final_buff1 = mock.final;
      res.coins_tax_buff1 = mock.tax;
    } else if (bIdx === 2) {
      res.coins_first_buff2 = mock.first;
      res.coins_final_buff2 = mock.final;
      res.coins_tax_buff2 = mock.tax;
    } else if (bIdx === 3) {
      res.coins_first_buff3 = mock.first;
      res.coins_final_buff3 = mock.final;
      res.coins_tax_buff3 = mock.tax;
    }
    return res;
  }
  const params = new URLSearchParams();
  if (friendName) params.set('name', friendName);
  if (buffIndex) params.set('buff', String(buffIndex));
  if (friendCode) params.set('buff_code', friendCode);
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) params.set('player_code', code);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest<FriendStatsResponse>(`/api/friend/stats${query}`);
}

export async function getPaymentStatus(): Promise<PaymentStatusResponse> {
  if (isDevMock()) {
    return { ...devPaymentStatus };
  }
  let query = '';
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) query = `?code=${encodeURIComponent(code)}`;
  }
  return apiRequest<PaymentStatusResponse>(`/api/payment${query}`);
}

export async function submitPayment(method: PaymentMethod): Promise<PaymentSubmitResponse> {
  if (isDevMock()) {
    devPaymentStatus = {
      ...devPaymentStatus,
      status: 'submitted',
    };
    return { success: true, status: 'submitted' };
  }
  let query = '';
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) query = `?code=${encodeURIComponent(code)}`;
  }
  return apiRequest<PaymentSubmitResponse>(`/api/payment/submit${query}`, {
    method: 'POST',
    body: JSON.stringify({ method }),
  });
}

export async function getBattleTimer(): Promise<BattleTimerResponse> {
  if (isDevMock()) {
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth();
    const year = now.getFullYear();

    let phase: BattlePhase;
    let targetDate: string;

    // Dev mock: simulate sacrifice pending for days 8-12
    if (day >= BATTLE_CYCLE.SACRIFICE_START_DAY && day <= BATTLE_CYCLE.SACRIFICE_END_DAY) {
      phase = 'sacrifice_pending';
      targetDate = new Date(year, month, BATTLE_CYCLE.SACRIFICE_END_DAY, 23, 59, 59).toISOString();
    } else if (day === BATTLE_CYCLE.FIRST_BATTLE_DAY) {
      phase = 'first_battle_day';
      targetDate = new Date(year, month, BATTLE_CYCLE.FIRST_BATTLE_DAY, 23, 59, 59).toISOString();
    } else if (day === BATTLE_CYCLE.FINAL_BATTLE_DAY) {
      phase = 'final_battle_day';
      targetDate = new Date(year, month, BATTLE_CYCLE.FINAL_BATTLE_DAY, 23, 59, 59).toISOString();
    } else if (day >= BATTLE_CYCLE.SACRIFICE_START_DAY && day < BATTLE_CYCLE.FIRST_BATTLE_DAY) {
      phase = 'waiting_first_battle';
      targetDate = new Date(year, month, BATTLE_CYCLE.FIRST_BATTLE_DAY, 0, 0, 0).toISOString();
    } else if (day > BATTLE_CYCLE.FIRST_BATTLE_DAY || day < BATTLE_CYCLE.FINAL_BATTLE_DAY) {
      phase = 'waiting_final_battle';
      if (day > BATTLE_CYCLE.FIRST_BATTLE_DAY) {
        targetDate = new Date(year, month + 1, BATTLE_CYCLE.FINAL_BATTLE_DAY, 0, 0, 0).toISOString();
      } else {
        targetDate = new Date(year, month, BATTLE_CYCLE.FINAL_BATTLE_DAY, 0, 0, 0).toISOString();
      }
    } else {
      phase = 'waiting_first_battle';
      targetDate = new Date(year, month, BATTLE_CYCLE.FIRST_BATTLE_DAY, 0, 0, 0).toISOString();
    }

    return { phase, targetDate };
  }
  return apiRequest<BattleTimerResponse>('/api/battle/timer');
}
